import { Anchor, Button, PinInput, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiError, officeApi } from '../api.js';
import { AuthScreen } from '../components/AuthScreen.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { FormHeader } from '../components/FormHeader.js';
import { PATHS, pathForStage } from '../routing.js';
import { useSession } from '../session.js';

export function TotpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // İstek sürerken kutular devre dışıdır, odak body'ye düşer: yanlış koddan sonra ilk haneye döner
  // ki yeniden yazılan kod kutulara girsin
  const first = useRef<HTMLInputElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (busy || !refocus.current) return;
    refocus.current = false;
    first.current?.focus();
  }, [busy]);

  const verify = async (value: string) => {
    if (value.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const { stage } = await officeApi.verifyTotp(value);
      session.setStage(stage);
      await session.refresh();
      navigate(pathForStage(stage), { replace: true });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
      setCode('');
      refocus.current = true;
    } finally {
      setBusy(false);
    }
  };

  const other = async () => {
    await officeApi.logout().catch(() => undefined);
    session.setStage(null);
    navigate(PATHS.login, { replace: true });
  };

  return (
    <AuthScreen>
      <FormHeader title={t('totp.title')} subtitle={t('totp.subtitle')} />
      <ErrorAlert code={error} />
      <Stack gap="lg">
        <div>
          {/* Görünen etiket kutu grubunun adıdır (WCAG 2.5.3); tıklanınca ilk hane odaklanır */}
          <Text
            component="label"
            htmlFor="totp-code-1"
            id="totp-code-label"
            size="sm"
            fw={500}
            mb={8}
            display="block"
          >
            {t('totp.code')}
          </Text>
          <PinInput
            ref={first}
            id="totp-code"
            aria-labelledby="totp-code-label"
            length={6}
            type="number"
            oneTimeCode
            size="md"
            autoFocus
            value={code}
            onChange={setCode}
            onComplete={(value) => void verify(value)}
            disabled={busy}
            getInputProps={(index) => ({ 'aria-label': t('totp.digit', { n: index + 1 }) })}
          />
        </div>
        <Button
          size="md"
          fullWidth
          disabled={code.length !== 6}
          loading={busy}
          onClick={() => void verify(code)}
        >
          {t('totp.submit')}
        </Button>
        <Anchor component="button" type="button" size="sm" ta="center" onClick={() => void other()}>
          {t('totp.other')}
        </Anchor>
      </Stack>
    </AuthScreen>
  );
}
